import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ListIcon } from 'lucide-react';
import { FC, memo } from 'react';
import { DriverEntity } from 'src/entities/Driver.entity';
import { HavePaginationEntity } from 'src/entities/HavePagination.entity';
import { PartnerCompanyEntity } from 'src/entities/PartnerCompanyEntity';
import { LaneManualAssignment, ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';

import { EditableAssignment } from '../hooks/useLaneDetailEdit';

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing?: boolean;
  manualAssignments?: EditableAssignment[];
  onUpdateAssignment?: (tempId: string, field: keyof Omit<LaneManualAssignment, 'id'>, value: string | number | null) => void;
  onAddAssignment?: () => void;
  onRemoveAssignment?: (tempId: string) => void;
  onDeleteAssignment?: (assignment: EditableAssignment) => void;
  /** 指定時は削除ボタンを disabled にし、素の title 属性で理由を出す（Task 10 #10）。 */
  deleteDisabledReason?: string;
  setTabValue: (value: number) => void;
  partnerCompanyData: HavePaginationEntity<PartnerCompanyEntity>;
  driverData: DriverEntity[];
};

/**
 * レーン詳細 - 配車情報タブ
 */
const LaneManualAssignments: FC<Props> = memo(({
  detail,
  isEditing = false,
  manualAssignments,
  onUpdateAssignment,
  onDeleteAssignment,
  deleteDisabledReason,
  setTabValue,
  partnerCompanyData,
  driverData,
}) => {
  return (
    <Box>
      {/* 車両スロット一覧 */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        配車情報
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 1 },
          // 読み取り専用時: disabled の文字色を通常と同じ濃さで表示
          ...(!isEditing && {
            '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' },
            '& .MuiSelect-select.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)' },
            '& .MuiFormLabel-root.Mui-disabled': { color: 'rgba(0, 0, 0, 0.6)' },
            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.23)' },
          }),
        }}
      >
        {manualAssignments.map((assignment, index) => (
          <Box
            key={assignment.tempId}
            sx={{
              minWidth: 350, // 固定幅に変更
              p: 3,
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: 1,
              '&:hover': { boxShadow: 3 }
            }}
          >
            {/* スロットヘッダー */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="bold">
                車両 {index + 1}
              </Typography>
              {/* disabledReason は素の title 属性で出す (disabled 要素は MUI Tooltip の
                  hover を拾えないため。ConvenienceDispatchToolbar.tsx:551 と同じ規則)。 */}
              <IconButton
                size="small"
                color="error"
                disabled={!!deleteDisabledReason}
                title={deleteDisabledReason}
                onClick={() => onDeleteAssignment?.(assignment)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            {/* 物量情報 */}
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                割り当て物量
              </Typography>
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1}>
                <Box textAlign="center">
                  <TextField
                    type="number"
                    value={assignment.quantity ?? ''}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      onUpdateAssignment?.(assignment.tempId, 'quantity', isNaN(val) ? null : val);
                    }}
                    size="small"
                    label="数量"
                    disabled={!isEditing}
                  />
                </Box>
                <Box textAlign="center">
                  <TextField
                    type="number"
                    value={assignment.weightKg ?? ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      onUpdateAssignment?.(assignment.tempId, 'weightKg', isNaN(val) ? null : val);
                    }}
                    size="small"
                    label="重量"
                    disabled={!isEditing}
                  />
                </Box>
                <Box textAlign="center">
                  <TextField
                    type="number"
                    value={assignment.volumeM3 ?? ''}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      onUpdateAssignment?.(assignment.tempId, 'volumeM3', isNaN(val) ? null : val);
                    }}
                    size="small"
                    label="体積"
                    disabled={!isEditing}
                  />
                </Box>
              </Box>
            </Box>

            {/* ③ 車両情報のアサイン */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Autocomplete
                freeSolo
                fullWidth
                disabled={!isEditing}
                options={partnerCompanyData?.data?.map((it) => it.name) || []}
                value={assignment.carrierCompany || null}
                onChange={(_, value) => {onUpdateAssignment?.(assignment.tempId, 'carrierCompany', value)}}
                onInputChange={(_, inputValue, reason) => {
                  if (reason === 'input') {
                    onUpdateAssignment?.(assignment.tempId, 'carrierCompany', inputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="運送会社"
                    placeholder="運送会社を選択または入力"
                    size="small"
                    disabled={!isEditing}
                  />
                )}
              />
              <Autocomplete
                freeSolo
                fullWidth
                disabled={!isEditing}
                options={partnerCompanyData?.data?.map((it) => it.name) || []}
                value={assignment.operatingCarrier || null}
                onChange={(_, value) => {onUpdateAssignment?.(assignment.tempId, 'operatingCarrier', value)}}
                onInputChange={(_, inputValue, reason) => {
                  if (reason === 'input') {
                    onUpdateAssignment?.(assignment.tempId, 'operatingCarrier', inputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="実走行会社"
                    placeholder="実走行会社を選択または入力"
                    size="small"
                    disabled={!isEditing}
                  />
                )}
              />
              <Autocomplete
                freeSolo
                fullWidth
                disabled={!isEditing}
                options={driverData || []}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                getOptionKey={(option) => typeof option === 'string' ? option : option.id}
                value={assignment.driverName || null}
                onChange={(_, value) => {
                  const name = typeof value === 'string' ? value : value?.name || null;
                  onUpdateAssignment?.(assignment.tempId, 'driverName', name);
                }}
                onInputChange={(_, inputValue, reason) => {
                  if (reason === 'input') {
                    onUpdateAssignment?.(assignment.tempId, 'driverName', inputValue);
                  }
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof option === 'string') return option === value;
                  if (typeof value === 'string') return option.name === value;
                  return option.id === value.id;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="ドライバー名"
                    placeholder="ドライバー名を選択または入力"
                    size="small"
                    disabled={!isEditing}
                  />
                )}
              />
              <TextField
                label="車番"
                value={assignment.truckNumber ?? ''}
                onChange={(e) => onUpdateAssignment?.(assignment.tempId, 'truckNumber', e.target.value)}
                size="small"
                disabled={!isEditing}
              />
              <FormControl size="small" disabled={!isEditing}>
                <InputLabel>請負階層</InputLabel>
                <Select
                  disabled={!isEditing}
                  value={assignment.hierarchyLevel ?? ''}
                  onChange={(e) => onUpdateAssignment?.(assignment.tempId, 'hierarchyLevel', e.target.value)}
                  label="請負階層"
                >
                  <MenuItem value="">元請け</MenuItem>
                  <MenuItem value="1">1次請け</MenuItem>
                  <MenuItem value="2">2次請け</MenuItem>
                  <MenuItem value="3">3次請け</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* 紐づいているLEG一覧 */}
            <Box mt={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ListIcon />}
                onClick={() => setTabValue(2)} // LEG一覧タブ（index 2）に移動
                fullWidth
              >
                LEG一覧 ({assignment.deliveryCount ?? detail.shipmentOrderLegs?.length ?? 0}件)
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default LaneManualAssignments;
